<script lang="ts">
// this must be exported here for the keep alive on reroute
export default {
  name: "ServiceTypes",
};
</script>

<script setup lang="ts">
import ServiceTypesTable from "@/components/models/service/ServiceTypesTable.vue";
import { useSnackStore } from "@/stores/snack";
import { useQuery } from "@tanstack/vue-query";
import { type AxiosResponse } from "axios";
import { definePage } from "unplugin-vue-router/runtime";
import { onMounted, provide, ref } from "vue";

import ServiceService, { type TServiceType } from "@/api/service";
import { ActionsEnum, SubjectsEnum, type AppAbility } from "@/services/ability";
import { generateFormOptions, type PaginatedType, type VFormOptions } from "@/utils/DjangoTypes";
import { useAbility } from "@casl/vue";
import { DefaultServerTableParams, type ServerTableParamsType } from "@/utils/TableUtils";

definePage({
  meta: {
    title: "Service Types",
    checkPrivileges: () => {
      const { can } = useAbility<AppAbility>();
      return can(ActionsEnum.USE, SubjectsEnum.SERVICE_TYPE);
    },
  },
});

const snackStore = useSnackStore();
const serviceTypeFetchEnabled = ref(false);

const serviceTypeOptions = ref<VFormOptions>({});

const queriesTableParams: Record<string, ServerTableParamsType> = {
  serviceTypes: DefaultServerTableParams,
};
provide("queriesTableParams", queriesTableParams);

const {
  data: serviceTypes,
  isPending: isPending,
  isFetching: isFetching,
} = useQuery({
  queryKey: ["serviceTypes"],
  queryFn: async (): Promise<PaginatedType<TServiceType[]> | undefined> => {
    try {
      const allRequests = Promise.all([
        ServiceService.serviceType.getAll(queriesTableParams.serviceTypes),
        ServiceService.serviceType.getOptions(),
      ]) as Promise<[AxiosResponse, AxiosResponse]>;

      const [result, options] = await allRequests;

      serviceTypeOptions.value = generateFormOptions(options.data.actions.POST);
      return result.data;
    } catch (error) {
      snackStore.showError(error);
    }
  },
  enabled: () => serviceTypeFetchEnabled.value,
});

onMounted(async () => {
  serviceTypeFetchEnabled.value = true;
});
</script>

<template>
  <v-container fluid>
    <div class="tw:!mb-10">
      <service-types-table
        :query-key="'serviceTypes'"
        :data="serviceTypes"
        :loading="isPending || isFetching"
        :options="serviceTypeOptions"
      />
    </div>
  </v-container>
</template>

<style scoped></style>
